import COLORS from '../styles/colors';
import { Button, Form, InputGroup, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import * as CONFIG from '../config';
import {
  quoteDataAtom,
  appRolesAtom,
  userIdAtom,
  paymentTypeLovAtom,
  saveQuoteTriggerAtom,
  quoteProcessorAtom,
  testingCostsAtom,
  initValuesAtom,
  depreciationMonthsLovAtom,
} from '../state';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useEffect } from 'react';
import cloneDeep from 'lodash.clonedeep';
import { getTestingCalculations } from '../helpers/calculations';


const QuoteTabTesting = () => {
  const quoteData = useRecoilValue(quoteDataAtom);
  const appRoles = useRecoilValue(appRolesAtom);
  const userId = useRecoilValue(userIdAtom);
  const depreciationMonthsLov = useRecoilValue(depreciationMonthsLovAtom);
  const paymentTypeLov = useRecoilValue(paymentTypeLovAtom);
  const quoteProcessor = useRecoilValue(quoteProcessorAtom);
  const saveQuoteTrigger = useRecoilValue(saveQuoteTriggerAtom);
  const [testingCosts, setTestingCosts] = useRecoilState(testingCostsAtom);
  const [initValues, setInitValues] = useRecoilState(initValuesAtom);
  
  useEffect(() => {
    if(saveQuoteTrigger) {
      quoteProcessor.setTestingData({
        testingCosts: testingCosts.map(o => ({
          description: o.description,
          paymentType: o.paymentType,
          quantity: o.quantity && !isNaN(parseInt(o.quantity, 10)) ? parseInt(o.quantity, 10) : null,
          unitCost: o.unitCost && !isNaN(parseFloat(o.unitCost)) ? parseFloat(o.unitCost) : null,
          unitPrice: o.unitPrice && !isNaN(parseFloat(o.unitPrice)) ? parseFloat(o.unitPrice) : null,
          depreciationMonths: o.depreciationMonths,
        })),
      });
    }
  }, [saveQuoteTrigger, quoteProcessor, testingCosts]);
  
  useEffect(() => {
    if(initValues && quoteData) {
      setTestingCosts(quoteData.testingCosts ? quoteData.testingCosts.map(o => ({
        description: o.description,
        paymentType: o.paymentType,
        quantity: o.quantity != null ? `${o.quantity}` : '',
        unitCost: o.unitCost != null ? `${o.unitCost}` : '',
        unitPrice: o.unitPrice != null ? `${o.unitPrice}` : '',
        depreciationMonths: o.depreciationMonths != null ? o.depreciationMonths : 'NULL',
      })) : []);
      setInitValues(false);
    }
  }, [initValues, quoteData, setTestingCosts, setInitValues]);
  
  const designAssignment = quoteData.assignments.find(o => o.types.includes('DESIGN_ESTIMATOR'));
  const designAssignedUserId = designAssignment ? designAssignment.userId : null;
  const feasibilityAssignment = quoteData.assignments.find(o => o.types.includes('FEASIBILITY'));
  const feasibilityAssignmentUserId = feasibilityAssignment ? feasibilityAssignment.userId : null;
  
  const disableAllInputs = quoteData.status !== 'SANDBOX' && (quoteData.holdUserId !== userId || quoteData.isNotActive || !['IN_PROGRESS', 'FEASIBILITY'].includes(quoteData.status) ||
    (!(appRoles.includes(CONFIG.ROLE_MAPPINGS.DESIGN_ESTIMATOR) && designAssignedUserId === userId) &&
      !(appRoles.includes(CONFIG.ROLE_MAPPINGS.FEASIBILITY) && userId === feasibilityAssignmentUserId)));
  
  const confirmRemoveTestingCost = index => {
    if(window.confirm('Are you sure you want to remove this testing cost?'))
      setTestingCosts(prevState => {
        const newState = cloneDeep(prevState);
        newState.splice(index, 1);
        return newState;
      });
  };
  
  const testingCalculations = getTestingCalculations({ testingCosts });
  
  return (
    <>
      <h5 style={{ ...styles.lineTitle, textAlign: 'left', marginTop: 20, marginBottom: 20 }}>
                  <span style={{ ...styles.lineTitleText, backgroundColor: COLORS.white }}>
                    Testing Costs
                  </span>
      </h5>
      <Table size="sm" style={{ marginBottom: 0 }}>
        <tbody>
        <tr style={{ fontWeight: 'bold' }}>
          <td>Test</td>
          <td>Quantity</td>
          <td>Cost</td>
          <td>Price</td>
          <td>Payment Type</td>
          <td>Depreciation (months)</td>
          <td>Total Cost</td>
          <td>Total Price</td>
          <td/>
        </tr>
        {testingCosts.map((testingCost, index) => (
          <tr key={index}>
            <td>
              <Form.Control
                type="text"
                value={testingCost.description}
                onChange={e => setTestingCosts(prevState => {
                  const newState = cloneDeep(prevState);
                  newState[index].description = e.target.value;
                  return newState;
                })}
                disabled={disableAllInputs}
              />
            </td>
            <td>
              <Form.Control
                type="number"
                step="1"
                min="1"
                value={testingCost.quantity}
                onChange={e => setTestingCosts(prevState => {
                  const newState = cloneDeep(prevState);
                  newState[index].quantity = e.target.value;
                  return newState;
                })}
                disabled={disableAllInputs}
              />
            </td>
            <td>
              <InputGroup>
                <InputGroup.Text style={styles.leftInputGroupText}>$</InputGroup.Text>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={testingCost.unitCost}
                  onChange={e => setTestingCosts(prevState => {
                    const newState = cloneDeep(prevState);
                    newState[index].unitCost = e.target.value;
                    return newState;
                  })}
                  disabled={disableAllInputs}
                />
              </InputGroup>
            </td>
            <td>
              <InputGroup>
                <InputGroup.Text style={styles.leftInputGroupText}>$</InputGroup.Text>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={testingCost.unitPrice}
                  onChange={e => setTestingCosts(prevState => {
                    const newState = cloneDeep(prevState);
                    newState[index].unitPrice = e.target.value;
                    return newState;
                  })}
                  disabled={disableAllInputs &&
                    !(['SALES_APPROVAL', 'COMPLETED'].includes(quoteData.status) &&
                      appRoles.includes(CONFIG.ROLE_MAPPINGS.CREATE_QUOTE) && userId === quoteData.createdBy && quoteData.holdUserId === userId)}
                />
              </InputGroup>
            </td>
            <td>
              <Form.Select
                as="select"
                value={testingCost.paymentType}
                onChange={e => setTestingCosts(prevState => {
                  const newState = cloneDeep(prevState);
                  newState[index].paymentType = e.target.value;
                  if(!e.target.value.startsWith('DEPRECIATE')) newState[index].depreciationMonths = 'NULL';
                  return newState;
                })}
                disabled={disableAllInputs &&
                  !(['SALES_APPROVAL', 'COMPLETED'].includes(quoteData.status) &&
                    appRoles.includes(CONFIG.ROLE_MAPPINGS.CREATE_QUOTE) && userId === quoteData.createdBy && quoteData.holdUserId === userId)}
              >
                <option value="NULL">-- Select --</option>
                {paymentTypeLov.filter(o => o.code !== 'CAP_INVESTMENT').map(o => <option key={o.code} value={o.code}>{o.name}</option>)}
              </Form.Select>
            </td>
            <td>
              <Form.Select
                as="select"
                value={testingCost.depreciationMonths}
                onChange={e => setTestingCosts(prevState => {
                  const newState = cloneDeep(prevState);
                  newState[index].depreciationMonths = e.target.value !== 'NULL' && !isNaN(parseInt(e.target.value, 10)) ?
                    parseInt(e.target.value, 10) : null;
                  return newState;
                })}
                disabled={!testingCost.paymentType.startsWith('DEPRECIATE') || (disableAllInputs &&
                  !(['SALES_APPROVAL', 'COMPLETED'].includes(quoteData.status) &&
                    appRoles.includes(CONFIG.ROLE_MAPPINGS.CREATE_QUOTE) && userId === quoteData.createdBy && quoteData.holdUserId === userId))}
              >
                <option value="NULL">-- Select --</option>
                {depreciationMonthsLov.map(o => <option key={o.code} value={o.code}>{o.name}</option>)}
              </Form.Select>
            </td>
            <td>
              <InputGroup>
                <InputGroup.Text style={styles.leftInputGroupText}>$</InputGroup.Text>
                <Form.Control
                  type="number"
                  value={testingCost.quantity && testingCost.quantity.length && !isNaN(parseInt(testingCost.quantity, 10)) &&
                  testingCost.unitCost && testingCost.unitCost.length && !isNaN(parseFloat(testingCost.unitCost)) ?
                    (parseInt(testingCost.quantity, 10) * parseFloat(testingCost.unitCost)).toFixed(5) : ''}
                  disabled
                />
              </InputGroup>
            </td>
            <td>
              <InputGroup>
                <InputGroup.Text style={styles.leftInputGroupText}>$</InputGroup.Text>
                <Form.Control
                  type="number"
                  value={testingCost.quantity && testingCost.quantity.length && !isNaN(parseInt(testingCost.quantity, 10)) &&
                  testingCost.unitPrice && testingCost.unitPrice.length && !isNaN(parseFloat(testingCost.unitPrice)) ?
                    (parseInt(testingCost.quantity, 10) * parseFloat(testingCost.unitPrice)).toFixed(5) : ''}
                  disabled
                />
              </InputGroup>
            </td>
            <td style={{ verticalAlign: 'middle' }}>
              {
                !disableAllInputs ?
                  <FontAwesomeIcon
                    icon={faTrashAlt}
                    className="delete-button"
                    style={styles.deleteButton}
                    onClick={() => confirmRemoveTestingCost(index)}
                  /> :
                  null
              }
            </td>
          </tr>
        ))}
        </tbody>
      </Table>
      <div style={styles.addMoldedPartButtonRow}>
        <Button
          type="button"
          variant="primary"
          onClick={() => setTestingCosts(prevState => {
            const newState = cloneDeep(prevState);
            newState.push({
              description: '',
              quantity: '',
              unitCost: '',
              paymentType: 'NULL',
              depreciationMonths: 'NULL',
            });
            return newState;
          })}
          disabled={disableAllInputs}
        >
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: 4 }}/>
          Add Test
        </Button>
      </div>
      <div style={styles.addMoldedPartButtonRow}>
        <p style={{ marginRight: 10, marginBottom: 0 }}>Total Testing Cost</p>
        <InputGroup style={{ flexBasis: '25%' }}>
          <InputGroup.Text style={styles.leftInputGroupText}>$</InputGroup.Text>
          <Form.Control
            type="number"
            value={testingCalculations?.totalTestingCost ? testingCalculations.totalTestingCost.toFixed(5) : ''}
            disabled
          />
        </InputGroup>
      </div>
      <div style={styles.addMoldedPartButtonRow}>
        <p style={{ marginRight: 10, marginBottom: 0 }}>Total Testing Price</p>
        <InputGroup style={{ flexBasis: '25%' }}>
          <InputGroup.Text style={styles.leftInputGroupText}>$</InputGroup.Text>
          <Form.Control
            type="number"
            value={testingCalculations?.totalTestingPrice ? testingCalculations.totalTestingPrice.toFixed(5) : ''}
            disabled
          />
        </InputGroup>
      </div>
      <div style={styles.addMoldedPartButtonRow}>
        <p style={{ marginRight: 10, marginBottom: 0 }}>Total Depreciated Into Cost</p>
        <InputGroup style={{ flexBasis: '25%' }}>
          <InputGroup.Text style={styles.leftInputGroupText}>$</InputGroup.Text>
          <Form.Control
            type="number"
            value={testingCalculations?.totalDepreciatedCost ? testingCalculations.totalDepreciatedCost.toFixed(5) : ''}
            disabled
          />
        </InputGroup>
      </div>
      <div style={styles.addMoldedPartButtonRow}>
        <p style={{ marginRight: 10, marginBottom: 0 }}>Total Depreciated Into Price</p>
        <InputGroup style={{ flexBasis: '25%' }}>
          <InputGroup.Text style={styles.leftInputGroupText}>$</InputGroup.Text>
          <Form.Control
            type="number"
            value={testingCalculations?.totalDepreciatedPrice ? testingCalculations.totalDepreciatedPrice.toFixed(5) : ''}
            disabled
          />
        </InputGroup>
      </div>
      <div style={styles.addMoldedPartButtonRow}>
        <p style={{ marginRight: 10, marginBottom: 0 }}>Total Expense</p>
        <InputGroup style={{ flexBasis: '25%' }}>
          <InputGroup.Text style={styles.leftInputGroupText}>$</InputGroup.Text>
          <Form.Control
            type="number"
            value={testingCalculations?.totalExpenseCost ? testingCalculations.totalExpenseCost.toFixed(5) : ''}
            disabled
          />
        </InputGroup>
      </div>
      <div style={styles.addMoldedPartButtonRow}>
        <p style={{ marginRight: 10, marginBottom: 0 }}>Total Customer Pays</p>
        <InputGroup style={{ flexBasis: '25%' }}>
          <InputGroup.Text style={styles.leftInputGroupText}>$</InputGroup.Text>
          <Form.Control
            type="number"
            value={testingCalculations?.totalCustomerPaysCost ? testingCalculations.totalCustomerPaysCost.toFixed(5) : ''}
            disabled
          />
        </InputGroup>
      </div>
    </>
  );
};

const styles = {
  lineTitle: {
    textAlign: 'center',
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: COLORS.midGray,
    lineHeight: '0.1em',
    marginTop: 12,
    marginBottom: 12,
    color: COLORS.darkGray,
  },
  lineTitleText: {
    backgroundColor: COLORS.offWhite,
    paddingLeft: 7,
    paddingRight: 7,
  },
  addMoldedPartButtonRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: 5,
    marginBottom: 8,
  },
  leftInputGroupText: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    borderRightWidth: 0,
  },
  deleteButton: {
    marginLeft: 5,
    cursor: 'pointer',
  },
};

export default QuoteTabTesting;